
import React from 'react';
import { ShieldCheck, Linkedin, Twitter, Github, ChevronUp, Youtube } from 'lucide-react';
import { COMPANY_NAME, NAV_ITEMS } from '../constants';
import AstraLogo from './AstraLogo';

interface FooterProps {
  onNavigate?: (view: 'home' | 'services', sectionId?: string) => void;
}

const Footer: React.FC<FooterProps> = ({ onNavigate }) => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLinkClick = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    const id = href.replace('#', '');
    if (onNavigate) {
        if (id === 'services') onNavigate('services');
        else onNavigate('home', id);
    }
  };

  return (
    <footer className="py-32 relative border-t border-white/5 bg-gradient-to-b from-transparent to-black">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-20 mb-32">
          <div className="col-span-2 space-y-10">
            <button onClick={() => onNavigate?.('home')} className="flex items-center gap-4 group">
              <AstraLogo size={56} />
              <span className="text-3xl font-black tracking-tighter text-white">SENTRIX ASTRA</span>
            </button>
            <p className="text-slate-500 max-w-sm leading-relaxed text-lg font-light">
              Architecting the ultimate defensive frameworks for planetary and orbital intelligence networks.
            </p>
            <div className="flex gap-6">
              {[
                { icon: <Linkedin className="w-5 h-5" />, color: 'hover:text-blue-400', href: 'https://www.linkedin.com/company/sentrix-astra-technologies' },
                { icon: <Youtube className="w-5 h-5" />, color: 'hover:text-red-500', href: 'https://www.youtube.com/channel/UCJhr_dKXsDOgoQZx0WlRjCQ' },
                { icon: <Twitter className="w-5 h-5" />, color: 'hover:text-brand-400', href: '#' },
                { icon: <Github className="w-5 h-5" />, color: 'hover:text-white', href: '#' }
              ].map((social, i) => (
                <a 
                  key={i} 
                  href={social.href} 
                  target={social.href !== '#' ? "_blank" : undefined}
                  rel={social.href !== '#' ? "noopener noreferrer" : undefined}
                  className={`w-12 h-12 rounded-xl glass-premium flex items-center justify-center border border-white/5 transition-all ${social.color} hover:scale-110`}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-black mb-10 uppercase text-[10px] tracking-[0.5em] text-slate-400">PROTOCOLS</h4>
            <ul className="space-y-6">
              {NAV_ITEMS.map(item => (
                <li key={item.label}>
                  <button 
                    onClick={(e) => handleLinkClick(e, item.href)} 
                    className="text-slate-500 hover:text-brand-400 transition-all text-sm font-black uppercase tracking-widest flex items-center gap-2 group"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-500/20 group-hover:bg-brand-500 transition-colors" />
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-black mb-10 uppercase text-[10px] tracking-[0.5em] text-slate-400">INTELLIGENCE</h4>
            <ul className="space-y-6">
              {['Astra Core', 'Defense Matrices', 'Neural Vaults', 'Orbital Relays'].map(link => (
                <li key={link}>
                  <a href="#" className="text-slate-500 hover:text-brand-400 transition-all text-sm font-black uppercase tracking-widest flex items-center gap-2 group">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-500/20 group-hover:bg-brand-500 transition-colors" />
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center pt-12 border-t border-white/5 gap-8">
          <div className="flex flex-col gap-2">
            <p className="text-slate-600 text-[10px] font-mono font-black uppercase tracking-[0.3em]">
              © {new Date().getFullYear()} {COMPANY_NAME.toUpperCase()}
            </p>
            <div className="flex gap-8">
              <a href="#" className="text-slate-700 hover:text-white text-[9px] font-black uppercase tracking-widest transition-colors">Privacy_Policy</a>
              <a href="#" className="text-slate-700 hover:text-white text-[9px] font-black uppercase tracking-widest transition-colors">Term_Sets</a>
              <a href="#" className="text-slate-700 hover:text-white text-[9px] font-black uppercase tracking-widest transition-colors">Security_Log</a>
            </div>
          </div>
          
          <button 
            onClick={scrollToTop}
            className="group p-4 rounded-2xl glass-premium border-white/5 hover:border-brand-500/30 transition-all flex items-center gap-3"
          >
            <span className="text-[10px] font-black text-slate-500 group-hover:text-brand-400 tracking-[0.3em] uppercase">Return_to_Core</span>
            <ChevronUp className="w-4 h-4 text-slate-600 group-hover:text-brand-400 group-hover:-translate-y-1 transition-all" />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
